import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import './analytics.scss';
import { Tabs } from 'antd';
import { ChartLine, Phone, UsersThree } from '@phosphor-icons/react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Typography from '../../components/Typography';
import COLORS from '../../util/constants/colors';
import ReferralStats from './ReferralStats';
import ReferralTrends from './ReferralTrends';
import TopProviders from './TopProviders';
import ReferralReportBuilder from './ReferralReportBuilder';
import { buildOwnerParams, csrfToken } from './helpers';
import CannotAccess from '../CannotAccess';
import { USER_ACTIVITY_ANALYTICS_FEATURE, verifyFeatureAccess } from '../../global/featureFlags';
import FilterBox from './FilterBox';
import MairaDashboard from './MairaDashboard';
import UserActivityDashboard from './UserActivity';
import { captureAnalytics } from '../../actions/preload';

const REFERRALS_TAB = 'referrals';
const MAIRA_TAB = 'maira';
const USER_ACTIVITY_TAB = 'user_activity';

const Analytics = ({ isAdmin }) => {
  const [loading, setLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState({});
  const [hasAnalyticsAccess, setHasAnalyticsAccess] = useState(null); // null = checking, true = has access, false = no access
  const dateFormat = 'YYYY-MM-DD';
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(7, 'days').format(dateFormat),
    endDate: moment().format(dateFormat),
  });
  const [ownerContacts, setOwnerContacts] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [hasReferralManagement, setHasReferralManagement] = useState(false);
  const [hasVoiceAi, setHasVoiceAi] = useState(null);
  const [hasUserActivity, setHasUserActivity] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    const fetchFeatureAccess = async () => {
      const [referralManagementAccess, voiceAiAccess, userActivityAccess] = await Promise.all([
        verifyFeatureAccess('referral_management'),
        verifyFeatureAccess('voice_ai'),
        verifyFeatureAccess(USER_ACTIVITY_ANALYTICS_FEATURE),
      ]);
      setHasReferralManagement(referralManagementAccess);
      setHasVoiceAi(voiceAiAccess);
      setHasUserActivity(userActivityAccess);
      setHasAnalyticsAccess(localStorage.getItem('m8_view_referral_analytics') === 'true');
    };

    fetchFeatureAccess();
  }, []);

  const canViewReferralAnalytics = useMemo(() => {
    return (isAdmin && hasReferralManagement) || hasAnalyticsAccess === true;
  }, [hasAnalyticsAccess, hasReferralManagement, isAdmin]);

  const canViewMairaAnalytics = useMemo(() => {
    return isAdmin && hasVoiceAi === true;
  }, [hasVoiceAi, isAdmin]);

  // ENG-3857: per-person work counts, so admins only, behind its own flag.
  const canViewUserActivity = isAdmin && hasUserActivity === true;

  const canAccessAnalytics = canViewReferralAnalytics || canViewMairaAnalytics || canViewUserActivity;

  useEffect(() => {
    if (hasAnalyticsAccess === null || hasVoiceAi === null || hasUserActivity === null) return;

    setActiveTab((currentTab) => {
      if (currentTab === REFERRALS_TAB && canViewReferralAnalytics) return currentTab;
      if (currentTab === MAIRA_TAB && canViewMairaAnalytics) return currentTab;
      if (currentTab === USER_ACTIVITY_TAB && canViewUserActivity) return currentTab;
      if (canViewReferralAnalytics) return REFERRALS_TAB;
      if (canViewMairaAnalytics) return MAIRA_TAB;
      if (canViewUserActivity) return USER_ACTIVITY_TAB;
      return null;
    });
  }, [canViewMairaAnalytics, canViewReferralAnalytics, canViewUserActivity, hasAnalyticsAccess, hasUserActivity, hasVoiceAi]);

  useEffect(() => {
    if (!canViewReferralAnalytics || activeTab !== REFERRALS_TAB) return;

    axios({
      url: '/api/v1/referral_analytics/owner_contacts',
      method: 'GET',
    }).then((response) => {
      setOwnerContacts(response.data);
    }).catch((error) => {
      console.error(error);
    });
  }, [activeTab, canViewReferralAnalytics]);

  const fetchReferralStats = () => {
    if (!canViewReferralAnalytics) return;

    setLoading(true);
    const owners = buildOwnerParams(selectedRecipients, ownerContacts);
    axios({
      method: 'GET',
      url: '/api/v1/referral_analytics',
      params: {
        start_date: dateRange?.startDate || null,
        end_date: dateRange?.endDate || null,
        owners,
      },
      headers: {
        'X-CSRF-Token': csrfToken(),
      },
    }).then((response) => {
      setAnalyticsData(response.data);
    }).catch((error) => {
      console.error(error);
    }).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    if (!canViewReferralAnalytics || activeTab !== REFERRALS_TAB) return;

    fetchReferralStats();
  }, [activeTab, canViewReferralAnalytics]);

  // Show loading while checking access
  if (hasAnalyticsAccess === null || hasVoiceAi === null) {
    return <div>Loading...</div>;
  }

  if (!canAccessAnalytics) {
    return (
      <CannotAccess />
    );
  }

  const defaultTab = [
    [REFERRALS_TAB, canViewReferralAnalytics],
    [MAIRA_TAB, canViewMairaAnalytics],
    [USER_ACTIVITY_TAB, canViewUserActivity],
  ].find(([, allowed]) => allowed)[0];
  const renderedActiveTab = activeTab || defaultTab;

  const changeTab = (tab) => {
    if (tab === USER_ACTIVITY_TAB) captureAnalytics('user_activity_tab_opened');
    setActiveTab(tab);
  };

  const clearParams = () => {
    setDateRange({
      startDate: null,
      endDate: null,
    });
    setSelectedRecipients([]);
  };

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <div className="analytics-header-left">
          <Typography variant="subtitleTwo">Analytics Dashboard</Typography>
          <Typography variant="text" size="small" bold color={COLORS.TEXT_TERTIARY_LITE}>
            Welcome back! Here&apos;s what&apos;s happening across your analytics.
          </Typography>
        </div>
        {renderedActiveTab === REFERRALS_TAB && (
          <div className="analytics-header-right">
            <FilterBox
              ownerContacts={ownerContacts}
              selectedRecipients={selectedRecipients}
              setSelectedRecipients={setSelectedRecipients}
              dateRange={dateRange}
              setDateRange={setDateRange}
              fetchReferralStats={fetchReferralStats}
              clearParams={clearParams}
            />
          </div>
        )}
        {renderedActiveTab === MAIRA_TAB && (
          <div className="analytics-header-right">
            <div id="maira-analytics-filters" className="maira-header-filter-target" />
          </div>
        )}
        {renderedActiveTab === USER_ACTIVITY_TAB && (
          <div className="analytics-header-right">
            <div id="user-activity-analytics-filters" className="maira-header-filter-target" />
          </div>
        )}
      </div>
      <Tabs
        className="analytics-tabs"
        activeKey={renderedActiveTab}
        onChange={changeTab}
        tabBarGutter={16}
      >
        {canViewReferralAnalytics && (
          <Tabs.TabPane
            tab={
              (
                <div className="analytics-tab-label">
                  <ChartLine size={16} weight="bold" />
                  Referrals Analytics
                </div>
              )
            }
            key={REFERRALS_TAB}
          >
            <ReferralStats analyticsData={analyticsData} loading={loading} />
            <div className="referral-trends-and-top-providers">
              <ReferralTrends referralTrends={analyticsData.referral_trends} loading={loading} />
              <TopProviders
                topProviders={analyticsData.top_referring_providers}
                lowestProviders={analyticsData.lowest_referring_providers}
                loading={loading}
              />
            </div>
            <ReferralReportBuilder ownerContacts={ownerContacts} />
          </Tabs.TabPane>
        )}
        {canViewMairaAnalytics && (
          <Tabs.TabPane
            tab={
              (
                <div className="analytics-tab-label">
                  <Phone size={16} weight="bold" />
                  MAIRA Analytics
                </div>
              )
            }
            key={MAIRA_TAB}
          >
            <MairaDashboard active={renderedActiveTab === MAIRA_TAB} />
          </Tabs.TabPane>
        )}
        {canViewUserActivity && (
          <Tabs.TabPane
            tab={
              (
                <div className="analytics-tab-label">
                  <UsersThree size={16} weight="bold" />
                  User Activity
                </div>
              )
            }
            key={USER_ACTIVITY_TAB}
          >
            <UserActivityDashboard active={renderedActiveTab === USER_ACTIVITY_TAB} />
          </Tabs.TabPane>
        )}
      </Tabs>
    </div>
  );
};

Analytics.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
};

export default connect((state) => ({
  isAdmin: state.auth.isAdmin,
}))(Analytics);
